import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem
} from "@/components/ui/sidebar";
import { useAccountStore } from "@/stores/account.store";
import { DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu";
import {
  ChevronsUpDown,
  HomeIcon,
  LogOut,
  Settings,
  Users,
  ShoppingBag,
  Shield
} from "lucide-react";
import { useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";

export function AppSidebar() {
  const { accountSnapshot, accountProxy } = useAccountStore();

  const location = useLocation();
  const handleLogOut = () => {
    accountProxy.logout();
  };

  useEffect(() => {
    if (accountSnapshot.isLogged && !accountSnapshot.organizationLogoUrl)
      accountProxy.getOrganizationLogoUrl();
  }, []);

  const mainGroupItems = [
    {
      title: "Home",
      to: "/",
      icon: HomeIcon
    },
    {
      title: "Organization",
      to: "/organization",
      icon: Shield
    },
    {
      title: "Products",
      to: "/products",
      icon: ShoppingBag
    },
    {
      title: "Users",
      to: "/users",
      icon: Users
    },
    {
      title: "User settings",
      to: "/settings",
      icon: Settings
    }
  ];

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <div>
                <div className="flex items-center justify-center rounded-lg aspect-square size-8 bg-sidebar-primary text-sidebar-primary-foreground">
                  <img
                    className="rounded-sm"
                    src={accountSnapshot.organizationLogoUrl}
                  ></img>
                </div>
                <div className="flex flex-col gap-0.5 leading-none">
                  <span className="font-semibold">
                    {
                      accountSnapshot.userInfo?.profile[
                        "urn:zitadel:iam:user:resourceowner:name"
                      ] as string
                    }
                  </span>
                </div>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainGroupItems.map(item => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={"/" + location.pathname.split("/")[1] == item.to}
                  >
                    <NavLink to={item.to}>
                      <item.icon />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="w-8 h-8 rounded-lg">
                    <AvatarImage src={accountProxy.userInfo?.profile.picture} />
                    <AvatarFallback className="rounded-lg">
                      {accountSnapshot.userInfo?.profile.given_name?.charAt(0)}
                      {accountSnapshot.userInfo?.profile.family_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-sm leading-tight text-left">
                    <span className="font-semibold truncate">
                      {accountSnapshot.userInfo?.profile.given_name +
                        " " +
                        accountSnapshot.userInfo?.profile.family_name}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-sm text-left">
                    <Avatar className="w-8 h-8 rounded-lg">
                      <AvatarImage
                        src={accountSnapshot.userInfo?.profile.picture}
                      />
                      <AvatarFallback className="rounded-lg">
                        {accountSnapshot.userInfo?.profile.given_name?.charAt(
                          0
                        )}
                        {accountSnapshot.userInfo?.profile.family_name?.charAt(
                          0
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-sm leading-tight text-left">
                      <span className="font-semibold truncate">
                        {accountSnapshot.userInfo?.profile.given_name +
                          " " +
                          accountSnapshot.userInfo?.profile.family_name}
                      </span>
                      <span className="text-xs truncate">
                        {accountSnapshot.userInfo?.profile.email}
                      </span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogOut}>
                  <LogOut />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
